import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Tooltip from "components/Tooltip";

import Button from "components/ui/Button";
import { SkeletonCard } from "../../../components/skeletons";
import { metalsService, categoriesService } from "../../../services/metalguard";

const BENCHMARK_BADGE = {
  LME: { label: "LME" },
  REGIONAL_INDEX: { label: "REGIONAL" },
  MANUAL: { label: "MANUAL" },
};

// Slider track style injected once
const SLIDER_STYLE = `
  .mg-slider {
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 4px;
    border-radius: 9999px;
    background: linear-gradient(
      to right,
      var(--color-accent) 0%,
      var(--color-accent) var(--val, 50%),
      var(--color-border) var(--val, 50%),
      var(--color-border) 100%
    );
    outline: none;
    cursor: pointer;
  }
  .mg-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-accent);
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px var(--color-accent);
    cursor: pointer;
    transition: box-shadow 0.15s;
  }
  .mg-slider::-webkit-slider-thumb:hover {
    box-shadow: 0 0 0 4px rgba(249,115,22,0.25);
  }
  .mg-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--color-accent);
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px var(--color-accent);
    cursor: pointer;
  }
  .mg-slider-warning {
    background: linear-gradient(
      to right,
      var(--color-warning) 0%,
      var(--color-warning) var(--val, 50%),
      var(--color-border) var(--val, 50%),
      var(--color-border) 100%
    );
  }
  .mg-slider-warning::-webkit-slider-thumb {
    background: var(--color-warning);
    box-shadow: 0 0 0 2px var(--color-warning);
  }
  .mg-slider-warning::-webkit-slider-thumb:hover {
    box-shadow: 0 0 0 4px rgba(245,158,11,0.25);
  }
  .mg-slider-warning::-moz-range-thumb {
    background: var(--color-warning);
    box-shadow: 0 0 0 2px var(--color-warning);
  }
`;

function sliderPercent(value, min, max) {
  return (((value - min) / (max - min)) * 100)?.toFixed(2) + "%";
}

export default function MetalInputForm({ formData, errors, onChange, onCalculate, onReset, isCalculating, onBenchmarkResolved, riskBufferResult: riskBufferResultProp, isFormReadyToCalculate, noBenchmarkWarning }) {
  const navigate = useNavigate();
  const [metals, setMetals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingMetals, setLoadingMetals] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [riskBufferResult, setRiskBufferResult] = useState(null);

  // Track whether targetMargin was auto-filled from category
  const [marginFromCategory, setMarginFromCategory] = useState(false);

  // Use prop from parent (async engine) if available, otherwise fall back to internal state
  const displayRiskBuffer = riskBufferResultProp !== undefined ? riskBufferResultProp : riskBufferResult;

  // Load metals on mount
  useEffect(() => {
    let cancelled = false;
    setLoadingMetals(true);
    metalsService?.getAll()?.then(({ data }) => {
      if (cancelled) return;
      setMetals(data || []);
      setLoadingMetals(false);
      // Auto-select first metal if none selected
      if (data?.length > 0 && !formData?.metalId) {
        handleMetalChange(data?.[0]);
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Load categories when metal changes
  useEffect(() => {
    if (!formData?.metalId) {
      setCategories([]);
      return;
    }
    let cancelled = false;
    setLoadingCategories(true);
    categoriesService?.getByMetal(formData?.metalId)?.then(({ data }) => {
      if (cancelled) return;
      setCategories(data || []);
      setLoadingCategories(false);
    });
    return () => { cancelled = true; };
  }, [formData?.metalId]);

  useEffect(() => {
    if (formData?.riskBufferMode === 'dynamic' && formData?.metalId && formData?.daysToSale) {
      // Trigger risk buffer calculation/resolution
      if (onBenchmarkResolved) {
        onBenchmarkResolved(formData)?.then((result) => {
          setRiskBufferResult(result?.riskBuffer || null);
        })?.catch(() => {
          setRiskBufferResult({ isFallback: true, fallbackReason: 'Error al calcular buffer dinámico', riskBufferPct: 3 });
        });
      }
    } else if (formData?.riskBufferMode === 'manual') {
      setRiskBufferResult(null);
    }
  }, [formData?.riskBufferMode, formData?.metalId, formData?.daysToSale, onBenchmarkResolved, formData]);

  const handleMetalChange = (metal) => {
    onChange({
      metalId: metal?.id,
      metal: metal?.name,
      metalBenchmarkType: metal?.benchmark_type,
      metalBenchmarkSymbol: metal?.benchmark_symbol,
      categoryId: "",
      category: "",
      yieldPct: formData?.yieldPct ?? 90,
      costLogistics: "",
      costProcessing: "",
      daysToSale: 30,
      discountMode: "PCT_OF_BENCHMARK",
      discountValue: 5,
      marginMode: 'PCT_OF_BENCHMARK',
    });
  };

  const handleCategoryChange = (cat) => {
    const discountMode = cat?.default_discount_mode ?? "PCT_OF_BENCHMARK";
    const discountValue = parseFloat(cat?.default_discount_value) ?? 5;
    // Pre-fill targetMargin from category discount value for both modes
    let newTargetMargin = formData?.targetMargin;
    let marginWasSet = false;
    let newMarginMode = 'PCT_OF_BENCHMARK';

    if (discountMode === 'PCT_OF_BENCHMARK' && !isNaN(discountValue) && discountValue > 0) {
      newTargetMargin = discountValue;
      newMarginMode = 'PCT_OF_BENCHMARK';
      marginWasSet = true;
    } else if (discountMode === 'FIXED_EUR_PER_TON' && !isNaN(discountValue) && discountValue > 0) {
      newTargetMargin = discountValue;
      newMarginMode = 'FIXED_EUR_PER_TON';
      marginWasSet = true;
    }

    setMarginFromCategory(marginWasSet);
    onChange({
      categoryId: cat?.id,
      category: cat?.display_name,
      yieldPct: parseFloat(cat?.default_yield_pct) ?? formData?.yieldPct,
      costProcessing: String(cat?.default_processing_cost_eur_per_ton ?? ""),
      costLogistics: String(cat?.default_logistics_cost_eur_per_ton ?? ""),
      daysToSale: cat?.default_processing_days ?? 30,
      discountMode,
      discountValue,
      targetMargin: newTargetMargin,
      marginMode: newMarginMode,
    });
  };

  const selectedMetal = metals?.find((m) => m?.id === formData?.metalId);
  const badge = BENCHMARK_BADGE?.[selectedMetal?.benchmark_type] || null;

  return (
    <div className="card p-4 md:p-6 h-full flex flex-col gap-5">
      {/* Inject slider styles once */}
      <style>{SLIDER_STYLE}</style>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex items-center justify-center w-9 h-9 rounded-lg" style={{ background: "var(--color-accent)" }}>
          <Icon name="Calculator" size={18} color="#fff" strokeWidth={2.5} />
        </div>
        <div>
          <h2 className="text-base md:text-lg font-heading font-semibold" style={{ color: "var(--color-text-primary)" }}>
            Parámetros del Lote
          </h2>
          <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>Configure los datos del lote a calcular</p>
        </div>
      </div>

      {/* No-benchmark warning banner */}
      {noBenchmarkWarning && (
        <div className="flex items-start gap-3 px-3 py-3 rounded-lg border" style={{ background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.35)' }}>
          <Icon name="AlertTriangle" size={16} color="var(--color-warning)" strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
          <p className="text-xs" style={{ color: 'var(--color-warning)', fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.5 }}>
            No hay benchmark disponible para este metal. Actualiza el precio en Mercado antes de calcular.{' '}
            <button
              type="button"
              onClick={() => window.location.href = '/market-data'}
              className="underline font-semibold"
              style={{ color: 'var(--color-warning)' }}
            >
              Ir a Mercado
            </button>
          </p>
        </div>
      )}

      {/* Metal Selector */}
      <div data-tour="calc-metal-selector">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Tipo de Metal"
              description="El tipo de metal determina el benchmark de precio (LME o índice interno) y el conjunto de categorías disponibles."
            >
              Tipo de Metal
            </Tooltip>
            {" "}<span style={{ color: "var(--color-error)" }}>*</span>
          </label>
          {badge && (
            <span
              className="text-xs font-medium px-2 py-0.5 rounded border"
              style={{
                background: "transparent",
                color: "var(--color-text-secondary)",
                borderColor: "var(--color-border)",
                fontFamily: "'IBM Plex Sans', sans-serif",
                letterSpacing: "0.04em",
              }}
            >
              {badge?.label}
            </span>
          )}
        </div>

        {loadingMetals ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {Array.from({ length: 6 })?.map((_, i) => (
              <SkeletonCard key={i} height="52px" rounded="rounded-lg" />
            ))}
          </div>
        ) : metals?.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-5 px-3 rounded-lg border border-dashed" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
            <Icon name="PackageOpen" size={22} color="var(--color-text-secondary)" strokeWidth={1.5} />
            <p className="text-sm text-center" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>No hay metales configurados.</p>
            <button
              onClick={() => navigate('/settings')}
              className="text-xs font-semibold underline"
              style={{ color: "var(--color-accent)", fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              Ir a Configuración para añadir metales
            </button>
          </div>
        ) : (
          <select
            value={formData?.metalId || ""}
            onChange={(e) => {
              const m = metals?.find((x) => x?.id === e?.target?.value);
              if (m) handleMetalChange(m);
            }}
            className="w-full px-3 py-2.5 rounded-lg border text-sm appearance-none cursor-pointer"
            style={{
              background: "var(--color-card)",
              color: formData?.metalId ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              borderColor: errors?.metal ? "var(--color-error)" : formData?.metalId ? "var(--color-accent)" : "var(--color-border)",
              fontFamily: "'IBM Plex Sans', sans-serif",
              outline: "none",
              boxShadow: errors?.metal ? "0 0 0 2px rgba(220,38,38,0.15)" : formData?.metalId ? "0 0 0 2px rgba(234,88,12,0.15)" : "none",
            }}
          >
            <option value="" disabled style={{ color: "var(--color-text-secondary)" }}>
              — Seleccionar metal —
            </option>
            {metals?.map((m) => (
              <option key={m?.id} value={m?.id}>
                {m?.name}
              </option>
            ))}
          </select>
        )}
        {errors?.metal && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.metal}</p>}
      </div>

      {/* Category Selector */}
      <div>
        <label className="block text-sm font-medium mb-2" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <Tooltip
            title="Categoría"
            description="La categoría define los parámetros por defecto: yield, descuentos, costes y días de proceso. Puedes sobreescribirlos para este lote."
          >
            Categoría
          </Tooltip>
          {" "}<span style={{ color: "var(--color-error)" }}>*</span>
        </label>

        {!formData?.metalId ? (
          <p className="text-sm py-2 px-3 rounded-lg border" style={{ color: "var(--color-text-secondary)", borderColor: "var(--color-border)", background: "var(--color-muted)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Seleccione un metal primero
          </p>
        ) : loadingCategories ? (
          <div className="flex flex-col gap-1.5">
            {Array.from({ length: 4 })?.map((_, i) => (
              <SkeletonCard key={i} height="38px" rounded="rounded-lg" />
            ))}
          </div>
        ) : categories?.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-5 px-3 rounded-lg border border-dashed" style={{ borderColor: "var(--color-border)", background: "var(--color-muted)" }}>
            <Icon name="FolderOpen" size={22} color="var(--color-text-secondary)" strokeWidth={1.5} />
            <p className="text-sm text-center" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>No hay categorías para este metal.</p>
            <button
              onClick={() => navigate('/settings')}
              className="text-xs font-semibold underline"
              style={{ color: "var(--color-accent)", fontFamily: "'IBM Plex Sans', sans-serif" }}
            >
              Ir a Configuración para añadir categorías
            </button>
          </div>
        ) : (
          <select
            value={formData?.categoryId || ""}
            onChange={(e) => {
              const cat = categories?.find((c) => c?.id === e?.target?.value);
              if (cat) handleCategoryChange(cat);
            }}
            className="w-full px-3 py-2.5 rounded-lg border text-sm appearance-none cursor-pointer"
            style={{
              background: "var(--color-card)",
              color: formData?.categoryId ? "var(--color-text-primary)" : "var(--color-text-secondary)",
              borderColor: errors?.category ? "var(--color-error)" : formData?.categoryId ? "var(--color-accent)" : "var(--color-border)",
              fontFamily: "'IBM Plex Sans', sans-serif",
              outline: "none",
              boxShadow: formData?.categoryId ? "0 0 0 2px rgba(234,88,12,0.15)" : "none",
            }}
          >
            <option value="" disabled style={{ color: "var(--color-text-secondary)" }}>
              — Seleccionar categoría —
            </option>
            {categories?.map((cat) => (
              <option key={cat?.id} value={cat?.id}>
                {cat?.display_name} · Yield: {parseFloat(cat?.default_yield_pct)?.toFixed(0)}%
              </option>
            ))}
          </select>
        )}
        {errors?.category && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.category}</p>}
      </div>

      {/* Weight */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-tour="calc-inputs">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Peso Bruto"
              description="Peso bruto del lote en kilogramos. Si el lote tiene impurezas, usa el campo Tara para descontarlas."
              example="1000 kg"
            >
              Peso Bruto (kg)
            </Tooltip>
            {" "}<span style={{ color: "var(--color-error)" }}>*</span>
          </label>
          <input
            type="number"
            placeholder="Ej: 1000"
            value={formData?.weightGross}
            onChange={(e) => {
              // Strip non-numeric characters, allow decimal point
              const raw = e?.target?.value?.replace(/[^0-9.]/g, '');
              onChange({ weightGross: raw });
            }}
            min={1}
            className={`w-full px-3 py-2 rounded-lg border text-sm`}
            style={{
              background: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              fontFamily: "'Roboto Mono', monospace",
              borderColor: errors?.weightGross ? 'var(--color-error)' : 'var(--color-border)',
              outline: 'none',
              boxShadow: errors?.weightGross ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
            }}
          />
          {errors?.weightGross && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.weightGross}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Tara / Impurezas"
              description="Peso no metálico del lote (tierra, plástico, humedad, embalaje). Se descuenta del peso bruto antes de calcular el precio."
              example="80 kg en un lote de 1000 kg"
            >
              Tara / Impurezas (kg)
            </Tooltip>
          </label>
          <input
            type="number"
            placeholder="Ej: 50"
            value={formData?.weightTare}
            onChange={(e) => {
              const raw = e?.target?.value?.replace(/[^0-9.]/g, '');
              onChange({ weightTare: raw });
            }}
            min={0}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{
              background: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              fontFamily: "'Roboto Mono', monospace",
              borderColor: errors?.weightTare ? 'var(--color-error)' : 'var(--color-border)',
              outline: 'none',
              boxShadow: errors?.weightTare ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
            }}
          />
          {errors?.weightTare && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.weightTare}</p>}
        </div>
      </div>

      {/* Yield Slider */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Yield Estimado"
              description="Porcentaje de metal recuperable tras el procesamiento. Un yield del 92% significa que de cada 100 kg de scrap se obtienen 92 kg de metal vendible."
              example="92%"
            >
              Yield Estimado
            </Tooltip>
          </label>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={50}
            max={99}
            step={0.5}
            value={formData?.yieldPct}
            onChange={(e) => onChange({ yieldPct: parseFloat(e?.target?.value) })}
            className="mg-slider flex-1"
            style={{ "--val": sliderPercent(formData?.yieldPct, 50, 99) }}
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0.1}
              max={100}
              step={0.5}
              value={formData?.yieldPct}
              onChange={(e) => {
                const v = parseFloat(e?.target?.value);
                if (!isNaN(v)) onChange({ yieldPct: v });
              }}
              className="w-16 px-2 py-1 rounded border text-xs text-center font-data font-semibold"
              style={{
                background: 'var(--color-card)',
                color: errors?.yieldPct ? 'var(--color-error)' : 'var(--color-accent)',
                borderColor: errors?.yieldPct ? 'var(--color-error)' : 'var(--color-border)',
                outline: 'none',
              }}
            />
            <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>%</span>
          </div>
        </div>
        <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <span>50%</span><span>75%</span><span>99%</span>
        </div>
        {errors?.yieldPct && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.yieldPct}</p>}
      </div>

      {/* Costs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Coste Logística"
              description="Coste de transporte, carga y descarga por tonelada."
              example="12 €/t"
            >
              Coste Logística (€/t)
            </Tooltip>
          </label>
          <input
            type="number"
            placeholder="Ej: 45"
            value={formData?.costLogistics}
            onChange={(e) => onChange({ costLogistics: e?.target?.value })}
            min={0}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{
              background: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              fontFamily: "'IBM Plex Mono', monospace",
              borderColor: errors?.costLogistics ? 'var(--color-error)' : 'var(--color-border)',
              outline: 'none',
              boxShadow: errors?.costLogistics ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
            }}
          />
          {errors?.costLogistics && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.costLogistics}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Coste Procesamiento"
              description="Coste de tratamiento y procesamiento del material por tonelada de peso neto."
              example="45 €/t"
            >
              Coste Procesamiento (€/t)
            </Tooltip>
          </label>
          <input
            type="number"
            placeholder="Ej: 80"
            value={formData?.costProcessing}
            onChange={(e) => onChange({ costProcessing: e?.target?.value })}
            min={0}
            className="w-full px-3 py-2 rounded-lg border text-sm"
            style={{
              background: 'var(--color-card)',
              color: 'var(--color-text-primary)',
              fontFamily: "'IBM Plex Mono', monospace",
              borderColor: errors?.costProcessing ? 'var(--color-error)' : 'var(--color-border)',
              outline: 'none',
              boxShadow: errors?.costProcessing ? '0 0 0 2px rgba(220,38,38,0.15)' : 'none',
            }}
          />
          {errors?.costProcessing && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.costProcessing}</p>}
        </div>
      </div>

      {/* Days to sale + Target margin */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <Tooltip
                title="Días hasta Venta"
                description="Días estimados desde la compra del lote hasta su venta. Afecta directamente al buffer de riesgo: más días = mayor exposición al movimiento de precios."
              >
                Días hasta Venta
              </Tooltip>
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={1}
              max={180}
              step={1}
              value={Math.min(180, Math.max(1, formData?.daysToSale || 1))}
              onChange={(e) => onChange({ daysToSale: parseInt(e?.target?.value) })}
              className="mg-slider flex-1"
              style={{ "--val": sliderPercent(Math.min(180, Math.max(1, formData?.daysToSale || 1)), 1, 180) }}
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1}
                max={365}
                step={1}
                value={formData?.daysToSale}
                onChange={(e) => {
                  const v = parseInt(e?.target?.value);
                  if (!isNaN(v)) onChange({ daysToSale: v });
                }}
                className="w-16 px-2 py-1 rounded border text-xs text-center font-data font-semibold"
                style={{
                  background: 'var(--color-card)',
                  color: errors?.daysToSale ? 'var(--color-error)' : 'var(--color-text-primary)',
                  borderColor: errors?.daysToSale ? 'var(--color-error)' : 'var(--color-border)',
                  outline: 'none',
                }}
              />
              <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>d</span>
            </div>
          </div>
          <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <span>1d</span><span>90d</span><span>180d</span>
          </div>
          {errors?.daysToSale && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.daysToSale}</p>}
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <Tooltip
                title="Margen Objetivo"
                description="Margen mínimo de beneficio que quieres garantizar por tonelada procesada. Se deduce del precio máximo de compra."
                example="80 €/t"
              >
                Margen Objetivo
              </Tooltip>
            </label>
            <div className="flex items-center gap-2">
              {/* Segmented mode toggle */}
              <div
                className="flex rounded overflow-hidden"
                style={{ border: '1px solid var(--color-border)', background: 'var(--color-surface)' }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (formData?.marginMode !== 'PCT_OF_BENCHMARK') {
                      setMarginFromCategory(false);
                      onChange({ marginMode: 'PCT_OF_BENCHMARK', targetMargin: 5 });
                    }
                  }}
                  className="px-2 py-0.5 text-xs font-medium transition-colors"
                  style={{
                    background: (formData?.marginMode ?? 'PCT_OF_BENCHMARK') === 'PCT_OF_BENCHMARK' ? 'var(--color-accent)' : 'transparent',
                    color: (formData?.marginMode ?? 'PCT_OF_BENCHMARK') === 'PCT_OF_BENCHMARK' ? '#fff' : 'var(--color-text-secondary)',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    cursor: 'pointer',
                    border: 'none',
                    outline: 'none',
                  }}
                >
                  %
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (formData?.marginMode !== 'FIXED_EUR_PER_TON') {
                      setMarginFromCategory(false);
                      onChange({ marginMode: 'FIXED_EUR_PER_TON', targetMargin: 80 });
                    }
                  }}
                  className="px-2 py-0.5 text-xs font-medium transition-colors"
                  style={{
                    background: formData?.marginMode === 'FIXED_EUR_PER_TON' ? 'var(--color-accent)' : 'transparent',
                    color: formData?.marginMode === 'FIXED_EUR_PER_TON' ? '#fff' : 'var(--color-text-secondary)',
                    fontFamily: "'IBM Plex Sans', sans-serif",
                    cursor: 'pointer',
                    border: 'none',
                    outline: 'none',
                  }}
                >
                  €/t
                </button>
              </div>
              {marginFromCategory && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded font-medium flex items-center gap-1"
                  style={{ background: 'rgba(234,88,12,0.1)', color: 'var(--color-accent)', fontFamily: "'IBM Plex Sans', sans-serif", border: '1px solid rgba(234,88,12,0.25)' }}
                >
                  <Icon name="Zap" size={10} strokeWidth={2.5} color="var(--color-accent)" />
                  Categoría
                </span>
              )}
            </div>
          </div>
          {(formData?.marginMode ?? formData?.marginMode) === 'FIXED_EUR_PER_TON' ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={2000}
                  step={1}
                  value={formData?.targetMargin}
                  onChange={(e) => {
                    const v = parseFloat(e?.target?.value);
                    if (!isNaN(v)) {
                      setMarginFromCategory(false);
                      onChange({ targetMargin: v, marginMode: 'FIXED_EUR_PER_TON' });
                    }
                  }}
                  className="w-24 px-2 py-1 rounded border text-xs text-center font-data font-semibold"
                  style={{
                    background: 'var(--color-card)',
                    color: errors?.targetMargin ? 'var(--color-error)' : 'var(--color-text-primary)',
                    borderColor: errors?.targetMargin ? 'var(--color-error)' : 'var(--color-border)',
                    outline: 'none',
                  }}
                />
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>€/t</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={30}
                step={0.5}
                value={formData?.targetMargin}
                onChange={(e) => {
                  setMarginFromCategory(false);
                  onChange({ targetMargin: parseFloat(e?.target?.value), marginMode: 'PCT_OF_BENCHMARK' });
                }}
                className="mg-slider flex-1"
                style={{ "--val": sliderPercent(formData?.targetMargin, 1, 30) }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={30}
                  step={0.5}
                  value={formData?.targetMargin}
                  onChange={(e) => {
                    const v = parseFloat(e?.target?.value);
                    if (!isNaN(v)) {
                      setMarginFromCategory(false);
                      onChange({ targetMargin: v, marginMode: 'PCT_OF_BENCHMARK' });
                    }
                  }}
                  className="w-16 px-2 py-1 rounded border text-xs text-center font-data font-semibold"
                  style={{
                    background: 'var(--color-card)',
                    color: errors?.targetMargin ? 'var(--color-error)' : 'var(--color-text-primary)',
                    borderColor: errors?.targetMargin ? 'var(--color-error)' : 'var(--color-border)',
                    outline: 'none',
                  }}
                />
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>%</span>
              </div>
            </div>
          )}
          {(formData?.marginMode ?? formData?.marginMode) === 'PCT_OF_BENCHMARK' && (
            <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <span>1%</span><span>15%</span><span>30%</span>
            </div>
          )}
          {errors?.targetMargin && <p className="text-xs mt-1 text-red-500" data-field-error style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{errors?.targetMargin}</p>}
        </div>
      </div>

      {/* Risk Buffer */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium" style={{ color: "var(--color-text-primary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
            <Tooltip
              title="Buffer de Riesgo"
              description="Dinámico: el sistema calcula el buffer usando la volatilidad histórica combinada del precio LME (USD) y del tipo de cambio EUR/USD real de cada día — no un tipo fijo — aplicando VaR al 95%. Manual: tú fijas el porcentaje de protección."
            >
              Buffer de Riesgo
            </Tooltip>
          </label>
          <div className="flex items-center gap-1 rounded-lg p-0.5 border" style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}>
            <button
              type="button"
              onClick={() => onChange({ riskBufferMode: 'dynamic' })}
              className="px-2.5 py-1 rounded-md text-xs font-semibold transition-standard"
              style={{
                background: formData?.riskBufferMode !== 'manual' ? 'var(--color-accent)' : 'transparent',
                color: formData?.riskBufferMode !== 'manual' ? '#fff' : 'var(--color-text-secondary)',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              Dinámico
            </button>
            <button
              type="button"
              onClick={() => onChange({ riskBufferMode: 'manual' })}
              className="px-2.5 py-1 rounded-md text-xs font-semibold transition-standard"
              style={{
                background: formData?.riskBufferMode === 'manual' ? 'var(--color-warning)' : 'transparent',
                color: formData?.riskBufferMode === 'manual' ? '#fff' : 'var(--color-text-secondary)',
                fontFamily: "'IBM Plex Sans', sans-serif",
              }}
            >
              Manual
            </button>
          </div>
        </div>

        {formData?.riskBufferMode === 'manual' ? (
          <div>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={15}
                step={0.5}
                value={formData?.riskBufferManualPct ?? 3}
                onChange={(e) => onChange({ riskBufferManualPct: parseFloat(e?.target?.value) })}
                className="mg-slider mg-slider-warning flex-1"
                style={{ "--val": sliderPercent(formData?.riskBufferManualPct ?? 3, 0, 15) }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={15}
                  step={0.5}
                  value={formData?.riskBufferManualPct ?? 3}
                  onChange={(e) => {
                    const v = parseFloat(e?.target?.value);
                    if (!isNaN(v) && v >= 0 && v <= 15) onChange({ riskBufferManualPct: v });
                  }}
                  className="w-16 px-2 py-1 rounded border text-xs text-center font-data font-semibold"
                  style={{ background: 'var(--color-card)', color: 'var(--color-warning)', borderColor: 'var(--color-border)', outline: 'none' }}
                />
                <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>%</span>
              </div>
            </div>
            <div className="flex justify-between text-xs mt-1" style={{ color: "var(--color-text-secondary)", fontFamily: "'IBM Plex Sans', sans-serif" }}>
              <span>0%</span><span>7.5%</span><span>15%</span>
            </div>
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
              Porcentaje fijo aplicado sobre benchmark × yield. Independiente de la volatilidad del mercado.
            </p>
          </div>
        ) : (
          <div className="rounded-lg p-3 border" style={{ background: 'rgba(99,102,241,0.05)', borderColor: 'rgba(99,102,241,0.2)' }}>
            {displayRiskBuffer && !displayRiskBuffer?.isFallback ? (
              <div className="space-y-1.5">
                {/* Sigma decomposition — only shown when historical EUR/USD data is available */}
                {displayRiskBuffer?.sigmaLmeUsd != null && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        <Tooltip
                          title="Volatilidad LME en USD"
                          description="Desviación estándar de los retornos diarios del precio LME en dólares en los últimos 90 días. Mide cuánto varía el precio del metal en su mercado de origen."
                        >
                          σ precio LME (USD)
                        </Tooltip>
                      </span>
                      <span className="font-data text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{displayRiskBuffer?.sigmaLmeUsd?.toFixed(3)}%</span>
                    </div>
                    {displayRiskBuffer?.sigmaEurUsd != null && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                          <Tooltip
                            title="Volatilidad EUR/USD"
                            description="Contribución de los movimientos históricos del tipo de cambio EUR/USD a la volatilidad total del precio en euros. Capturada usando el tipo de cambio real de cada día histórico, no un valor fijo."
                          >
                            σ EUR/USD
                          </Tooltip>
                        </span>
                        <span className="font-data text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{displayRiskBuffer?.sigmaEurUsd?.toFixed(3)}%</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                        <Tooltip
                          title="Volatilidad Combinada en EUR"
                          description="Desviación estándar real de los retornos diarios del precio en EUR, calculada como LME_USD_día / EURUSD_día. Captura simultáneamente la volatilidad del metal y del tipo de cambio y su correlación. Es la sigma que se usa en el VaR al 95%."
                        >
                          σ combinada (EUR)
                        </Tooltip>
                      </span>
                      <span className="font-data text-xs font-bold" style={{ color: 'var(--color-accent)' }}>{displayRiskBuffer?.sigmaCombined?.toFixed(3)}%</span>
                    </div>
                    <div className="border-t my-1" style={{ borderColor: 'rgba(99,102,241,0.15)' }} />
                  </>
                )}
                {displayRiskBuffer?.sigmaLmeUsd == null && (
                  <div className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Volatilidad diaria (σ)</span>
                    <span className="font-data text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{displayRiskBuffer?.dailyVolatilityPct?.toFixed(3)}%</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Volatilidad período ({formData?.daysToSale}d)</span>
                  <span className="font-data text-xs font-semibold" style={{ color: 'var(--color-text-primary)' }}>{displayRiskBuffer?.periodVolatilityPct?.toFixed(2)}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Movimiento adverso (VaR 95%)</span>
                  <span className="font-data text-xs font-bold" style={{ color: 'var(--color-accent)' }}>{displayRiskBuffer?.adverseMovePct?.toFixed(2)}%</span>
                </div>
                <div className="flex items-center justify-between pt-1 border-t" style={{ borderColor: 'rgba(99,102,241,0.2)' }}>
                  <span className="text-xs font-semibold" style={{ color: 'var(--color-text-primary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Buffer resultante</span>
                  <span className="font-data text-sm font-bold" style={{ color: 'var(--color-accent)' }}>{displayRiskBuffer?.riskBufferPct?.toFixed(2)}%</span>
                </div>
                <p className="text-xs pt-1" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Basado en {displayRiskBuffer?.historyPointsUsed} puntos históricos · σ×√{formData?.daysToSale}×1.65
                </p>
                {/* Tooltip explaining combined sigma */}
                <div className="rounded-md px-2.5 py-2 mt-1" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.15)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif", lineHeight: 1.5 }}>
                    El buffer incluye tanto la volatilidad del precio LME en USD como los movimientos históricos del tipo de cambio EUR/USD. Ambas fuentes de riesgo están capturadas en la sigma combinada.
                  </p>
                </div>
              </div>
            ) : displayRiskBuffer?.isFallback ? (
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={14} color="var(--color-warning)" strokeWidth={2} />
                <div>
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-warning)', fontFamily: "'IBM Plex Sans', sans-serif" }}>Usando buffer conservador: 3%</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>{displayRiskBuffer?.fallbackReason}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Icon name="Info" size={14} color="var(--color-accent)" strokeWidth={2} />
                <p className="text-xs" style={{ color: 'var(--color-text-secondary)', fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  Se calculará automáticamente al seleccionar metal y días hasta venta
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-2 mt-auto">
        <Button
          variant="default"
          size="lg"
          fullWidth
          iconName={isCalculating ? undefined : "Calculator"}
          iconPosition="left"
          loading={isCalculating}
          disabled={isCalculating || isFormReadyToCalculate === false}
          onClick={onCalculate}
          style={isFormReadyToCalculate === false ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          {isCalculating ? 'Calculando...' : 'Calcular Precio'}
        </Button>
        <Button
          variant="outline"
          size="lg"
          iconName="RotateCcw"
          iconPosition="left"
          onClick={onReset}
        >
          Limpiar
        </Button>
      </div>
    </div>
  );
}